import React, { useCallback, useMemo, useState } from 'react';

import ReactPaginate from 'react-paginate';
import groupBy from 'lodash/groupBy';
import moment from 'moment-timezone';
import { Glyphicon } from 'react-bootstrap';

import { Availabilities } from '../../../../../types/Activity';
import { dateFormatter, getAvailableText, timeFormatter } from '../../helpers';

import S from './AvailabilitiesRows.styles';
import AvailabilitiesItem from './components';

type Props = {
  availabilities: Availabilities[];
  existingBookingOption: Availabilities | null;
  numOfAttendees: number;
  tz: string;
  isLoading: boolean;
  selectedAvailability: Availabilities | null;
  setSelectedAvailability(availability: Availabilities | null): void;
  isPerPerson: boolean;
};

type AvailabilitiesObject = {
  [k: string]: Availabilities[];
};

const AvailabilitiesRows = ({
  availabilities,
  existingBookingOption,
  numOfAttendees,
  tz,
  isLoading,
  selectedAvailability,
  setSelectedAvailability,
  isPerPerson,
}: Props) => {
  const data: AvailabilitiesObject = groupBy(
    availabilities.map(item => ({
      ...item,
      displayDate: dateFormatter(item.eventDate, tz),
    })),
    'displayDate'
  );

  const perPage = 4;

  const [page, setPage] = useState<number>(0);

  const pages = useMemo(() => {
    return Object.keys(data).length / perPage;
  }, [data, perPage]);

  const handlePageClick = event => {
    const page = event.selected;
    setPage(page);
  };

  const renderAvailabilities = useCallback(
    (item: Availabilities, index) => {
      const selected = selectedAvailability?.eventDate === item.eventDate;
      const { eventDate, capacity } = item;
      const disabled = !capacity || capacity < numOfAttendees;
      const text = `${timeFormatter(eventDate, tz)} ${getAvailableText(
        capacity,
        isPerPerson
      )}`;
      return (
        <AvailabilitiesItem
          setSelectedAvailability={setSelectedAvailability}
          disabled={disabled}
          text={text}
          selected={selected}
          key={item.eventDate + index}
          item={item}
        />
      );
    },
    [isPerPerson, selectedAvailability?.eventDate, setSelectedAvailability, tz,numOfAttendees]
  );

  const renderData = useMemo(() => {
    const _availabilities = Object.entries(data);
    return (
      <S.AvailabilitiesContainer>
        {_availabilities
          .slice(page * perPage, (page + 1) * perPage)
          .map(([key, value]) => (
            <S.Row key={key}>
              <h4>{key}</h4>
              {value.map(renderAvailabilities)}
            </S.Row>
          ))}
      </S.AvailabilitiesContainer>
    );
  }, [data, page, renderAvailabilities]);

  const { eventDate, capacity } = existingBookingOption ?? {
    eventDate: undefined,
    capacity: undefined,
  };

  if (isLoading) {
    return (
      <Glyphicon
        glyph='refresh'
        style={{
          display: 'block',
          marginLeft: 'auto',
          width: '50px',
          height: '50px',
          marginRight: 'auto',
        }}
        className='spinning'
      />
    );
  }

  const selected = selectedAvailability?.eventDate === eventDate;

  return (
    <S.Container>
      {(!!eventDate || !!capacity) && (
        <S.ExistingBookingOption
          onClick={() => {
            setSelectedAvailability({ eventDate, capacity });
          }}
          className='btn btn-primary'
          selected={selected}
        >
          Existing Booking Option: {moment(eventDate).tz(tz).format('dddd LT')}{' '}
          {getAvailableText(capacity, isPerPerson)}
        </S.ExistingBookingOption>
      )}
      <h4>Other Availabilities</h4>
      {renderData}
      {pages > 1 && (
        <ReactPaginate
          previousLabel={'<'}
          nextLabel={'>'}
          pageCount={pages}
          onPageChange={handlePageClick}
          containerClassName={'pagination'}
          activeClassName={'active'}
        />
      )}
    </S.Container>
  );
};
export default AvailabilitiesRows;
